/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * "Remote access" on a deployment's page: open SSH/RDP to its VMs for 1 to N minutes from the Swift host
 * and the admin addresses, see what is open and for how long, close it early, and open each platform's
 * own console. Swift also opens it by itself for a few minutes when a deployment (cut-over, failover,
 * failback) completes.
 */

import * as React from "react";
import styled from "styled-components";
import { ExternalLink, KeyRound, LoaderCircle, LockOpen, Lock, TerminalSquare } from "lucide-react";

import { ActionButton, SectionCard } from "@src/components/ui/DetailsKit";
import { Pill, T9, formatDateTime } from "@src/components/ui/Lists/ListTable";
import accessSource from "@src/sources/AccessSource";
import notificationStore from "@src/stores/NotificationStore";

import type { AccessGrant, DeploymentAccess } from "@src/sources/AccessSource";

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;
const Vm = styled.div`
  border-top: 1px solid ${T9.slate200};
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  &:first-of-type {
    border-top: none;
    padding-top: 4px;
  }
`;
const Name = styled.div`
  font-weight: 600;
  color: ${T9.slate900};
`;
const Small = styled.div`
  font-size: 12px;
  line-height: 17px;
  color: ${T9.slate500};
`;
const Select = styled.select`
  height: 32px;
  border: 1px solid ${T9.slate300};
  border-radius: 6px;
  padding: 0 8px;
  background: var(--t9-bg-surface);
  color: ${T9.slate900};
`;
const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${T9.brand600};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const portName = (p: number) => (p === 22 ? "SSH" : p === 3389 ? "RDP" : `TCP ${p}`);

export const formatLeft = (seconds: number | null | undefined) => {
  if (seconds == null) return "";
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

type Props = {
  deploymentId: string;
  /** Only completed deployments have VMs to reach. */
  completed: boolean;
  /** For tests. */
  pollMs?: number;
};

const AccessCard = (props: Props) => {
  const [data, setData] = React.useState<DeploymentAccess | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [minutes, setMinutes] = React.useState<number | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [tick, setTick] = React.useState(0);

  const load = React.useCallback(async () => {
    try {
      const out = await accessSource.get(props.deploymentId);
      setData(out);
      setError(null);
    } catch (err: any) {
      setError(err?.data?.error?.message || err?.message || "Could not load remote access.");
    }
  }, [props.deploymentId]);

  React.useEffect(() => {
    if (!props.completed) return undefined;
    load();
    const timer = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [props.completed, load]);

  const active = (data?.grants || []).filter(g => g.state === "open" || g.state === "pending");
  React.useEffect(() => {
    // Refresh every 10 s while something is open, else every minute.
    const every = active.length ? 10 : 60;
    if (tick && tick % every === 0) load();
  }, [tick]);

  if (!props.completed) return null;
  if (error && !data) {
    return (
      <SectionCard title="Remote access" icon={KeyRound} testId="accessCard">
        <Small>{error}</Small>
      </SectionCard>
    );
  }
  if (!data) return null;
  if (!data.enabled) {
    return (
      <SectionCard title="Remote access" icon={KeyRound} testId="accessCard">
        <Small>Temporary SSH/RDP access is switched off in SwiftIQ.</Small>
      </SectionCard>
    );
  }

  const chosen = minutes ?? data.default_minutes;
  const open = async () => {
    setBusy("open");
    try {
      const grants = await accessSource.open(props.deploymentId, chosen);
      const failed = grants.filter(g => g.state === "failed");
      if (failed.length) {
        notificationStore.alert(`Access could not be opened: ${failed[0].error}`, "error");
      } else {
        notificationStore.alert(`Access open for ${chosen} min`, "success");
      }
      await load();
    } catch (err: any) {
      notificationStore.alert(`Remote access: ${err?.data?.error?.message || err?.message || err}`, "error");
    } finally {
      setBusy(null);
    }
  };
  const close = async (grant: AccessGrant) => {
    setBusy(grant.id);
    try {
      await accessSource.close(props.deploymentId, grant.id);
      notificationStore.alert("Access closed", "success");
      await load();
    } catch (err: any) {
      notificationStore.alert(`Remote access: ${err?.data?.error?.message || err?.message || err}`, "error");
    } finally {
      setBusy(null);
    }
  };

  const secondsLeft = (g: AccessGrant) => {
    if (g.state !== "open" || !g.expires_at) return null;
    const until = Date.parse(g.expires_at);
    return Number.isNaN(until) ? g.seconds_left : Math.max(0, (until - Date.now()) / 1000);
  };

  return (
    <SectionCard
      title="Remote access"
      icon={KeyRound}
      testId="accessCard"
      subtitle={
        data.allowed.length
          ? `SSH (Linux) or RDP (Windows) for a few minutes from ${data.allowed
              .map(a => `${a.cidr} (${a.source})`)
              .join(", ")}. SwiftIQ closes it when the time is up.`
          : "No address to allow: SwiftIQ could not find its public address and no admin address is set."
      }
      actions={
        <Row>
          <Select
            aria-label="Minutes"
            data-test-id="access-minutes"
            value={chosen}
            onChange={e => setMinutes(Number(e.target.value))}
          >
            {Array.from({ length: data.max_minutes }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>
                {m} min
              </option>
            ))}
          </Select>
          <ActionButton
            type="button"
            onClick={open}
            disabled={busy !== null || !data.allowed.length || !data.vms.length}
            data-test-id="access-open"
          >
            {busy === "open" ? <LoaderCircle size={16} className="spin" /> : <LockOpen size={16} />}
            {active.length ? "Open again" : "Open access"}
          </ActionButton>
        </Row>
      }
    >
      {data.vms.length ? null : <Small>This deployment has no deployed VM.</Small>}
      {data.vms.map(vm => {
        const grants = data.grants.filter(g => g.instance_id === vm.instance_id);
        const current = grants.find(g => g.state === "open" || g.state === "pending" || g.state === "close_failed");
        const last = grants[0];
        const left = current ? secondsLeft(current) : null;
        const ips = current?.result || last?.result || {};
        return (
          <Vm key={vm.instance_id} data-test-id="access-vm">
            <Row>
              <Name>{vm.name}</Name>
              <Small>{vm.ports.map(portName).join(", ")}</Small>
              {current?.state === "open" ? (
                <Pill tone="success" icon={LockOpen}>
                  Open · {formatLeft(left)} left
                </Pill>
              ) : current?.state === "pending" ? (
                <Pill tone="info" icon={LoaderCircle} spin>
                  Opening
                </Pill>
              ) : current?.state === "close_failed" ? (
                <Pill tone="danger" title={current.error || undefined}>
                  Closing failed: retrying
                </Pill>
              ) : (
                <Pill tone="neutral" icon={Lock}>
                  Closed
                </Pill>
              )}
              {current && current.state !== "pending" ? (
                <ActionButton
                  type="button"
                  variant="secondary"
                  onClick={() => close(current)}
                  disabled={busy !== null}
                  data-test-id="access-close"
                >
                  {busy === current.id ? <LoaderCircle size={16} className="spin" /> : <Lock size={16} />}
                  Close now
                </ActionButton>
              ) : null}
            </Row>
            {ips.public_ip || ips.private_ip ? (
              <Small>
                {ips.public_ip ? `Public IP ${ips.public_ip}` : ""}
                {ips.public_ip && ips.private_ip ? " · " : ""}
                {ips.private_ip ? `private IP ${ips.private_ip}` : ""}
              </Small>
            ) : null}
            {current?.result?.applied?.length ? <Small>Allowed: {current.result.applied.join("; ")}</Small> : null}
            {current?.result?.already?.length ? (
              <Small>Already allowed before: {current.result.already.join("; ")}</Small>
            ) : null}
            {(current || last)?.result?.notes?.map(n => <Small key={n}>{n}</Small>)}
            {last && last.state === "failed" ? <Small>Last attempt failed: {last.error}</Small> : null}
            {last && last.state === "closed" && !current ? (
              <Small>
                Last opened {formatDateTime(last.opened_at)} for {last.minutes} min
                {last.trigger === "deployment" ? " (after the deployment)" : last.requested_by ? ` by ${last.requested_by}` : ""}
                ; closed {formatDateTime(last.closed_at)}
                {last.closed_by ? ` (${last.closed_by})` : ""}.
              </Small>
            ) : null}
            {vm.console?.url ? (
              <Row>
                <LinkButton href={vm.console.url} target="_blank" rel="noopener noreferrer" data-test-id="access-console">
                  <TerminalSquare size={14} /> {vm.console.label || "Console"} <ExternalLink size={12} />
                </LinkButton>
                {(vm.console.extra || []).map(x => (
                  <LinkButton key={x.url} href={x.url} target="_blank" rel="noopener noreferrer">
                    {x.label} <ExternalLink size={12} />
                  </LinkButton>
                ))}
              </Row>
            ) : null}
            {vm.console?.notes?.map(n => <Small key={n}>{n}</Small>)}
          </Vm>
        );
      })}
    </SectionCard>
  );
};

export default AccessCard;
